import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck, LogOut, Loader2, Inbox, FileText, CalendarClock, MessageSquare,
  Phone, Mail, Building2, MapPin, Search, ArrowLeft,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { COMPANY } from "@/lib/constants";

const STATUS_STYLES = {
  new: "bg-sky-500/15 text-sky-300 border-sky-500/40",
  contacted: "bg-amber-500/15 text-amber-300 border-amber-500/40",
  scheduled: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  completed: "bg-indigo-500/15 text-indigo-300 border-indigo-500/40",
  archived: "bg-slate-500/15 text-slate-300 border-slate-500/40",
};
const STATUSES = ["new", "contacted", "scheduled", "completed", "archived"];
const TYPE_META = {
  quote: { label: "Quote", icon: FileText, color: "text-[#38BDF8]" },
  survey: { label: "Survey", icon: CalendarClock, color: "text-emerald-400" },
  contact: { label: "Contact", icon: MessageSquare, color: "text-amber-400" },
};

export default function Dashboard() {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-[#050B18] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#38BDF8]" />
      </div>
    );
  }
  return user ? <DashboardView /> : <LoginView />;
}

function LoginView() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email, password);
      toast.success("Welcome back!");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050B18] grid-overlay flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 text-sm text-slate-400 hover:text-[#38BDF8] mb-6 transition-colors">
          <ArrowLeft className="h-4 w-4" /> Back to site
        </Link>
        <div className="rounded-2xl border border-slate-700/80 bg-[#0A142F] p-8 glow-blue">
          <div className="flex items-center gap-3 mb-6">
            <img src={COMPANY.logo} alt="Atlas" className="h-12 w-12 object-contain" />
            <div>
              <div className="font-display font-extrabold tracking-wider metallic-text text-lg">ATLAS</div>
              <div className="text-[9px] tracking-[0.22em] text-[#38BDF8] font-semibold">OWNER PORTAL</div>
            </div>
          </div>
          <h1 className="font-display text-2xl font-bold text-white mb-1">Owner Login</h1>
          <p className="text-sm text-slate-400 mb-6">Sign in to view leads and survey bookings.</p>

          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-slate-400 uppercase tracking-wide">Email</Label>
              <Input data-testid="owner-login-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin email" className="bg-[#050B18] border-slate-700" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-slate-400 uppercase tracking-wide">Password</Label>
              <Input data-testid="owner-login-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="bg-[#050B18] border-slate-700" />
            </div>
            {error && <p data-testid="owner-login-error" className="text-sm text-red-400">{error}</p>}
            <Button data-testid="owner-dashboard-login-submit-btn" type="submit" disabled={busy} className="w-full bg-[#0066FF] hover:bg-[#0052cc] text-white font-semibold h-11 glow-blue">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign In"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}

function DashboardView() {
  const { user, logout } = useAuth();
  const [subs, setSubs] = useState([]);
  const [stats, setStats] = useState({ total: 0, quotes: 0, surveys: 0, new: 0 });
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [subRes, statRes] = await Promise.all([
        api.get("/submissions", { params: { type: typeFilter, status: statusFilter } }),
        api.get("/submissions/stats"),
      ]);
      setSubs(subRes.data);
      setStats(statRes.data);
    } catch (err) {
      toast.error("Failed to load leads");
    } finally {
      setLoading(false);
    }
  }, [typeFilter, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/submissions/${id}/status`, { status });
      setSubs((prev) => prev.map((s) => (s.id === id ? { ...s, status } : s)));
      const statRes = await api.get("/submissions/stats");
      setStats(statRes.data);
      toast.success(`Marked as ${status}`);
    } catch {
      toast.error("Could not update status");
    }
  };

  const filtered = subs.filter((s) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return [s.name, s.email, s.phone, s.company, s.message, s.site_address].filter(Boolean).some((v) => v.toLowerCase().includes(q));
  });

  const metrics = [
    { label: "Total Leads", value: stats.total, icon: Inbox, color: "text-[#38BDF8]" },
    { label: "Quote Requests", value: stats.quotes, icon: FileText, color: "text-[#0066FF]" },
    { label: "Survey Bookings", value: stats.surveys, icon: CalendarClock, color: "text-emerald-400" },
    { label: "New / Unactioned", value: stats.new, icon: MessageSquare, color: "text-amber-400" },
  ];

  return (
    <div className="min-h-screen bg-[#050B18]">
      <header className="glass border-b border-slate-800/80 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3.5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={COMPANY.logo} alt="Atlas" className="h-10 w-10 object-contain" />
            <div>
              <div className="font-display font-extrabold tracking-wider metallic-text leading-none">ATLAS</div>
              <div className="text-[9px] tracking-[0.2em] text-[#38BDF8] font-semibold">OWNER PORTAL</div>
            </div>
          </Link>
          <div className="flex items-center gap-4">
            <span className="hidden sm:block text-sm text-slate-400">{user?.email}</span>
            <Button data-testid="dashboard-logout-btn" onClick={logout} variant="outline" size="sm" className="border-slate-600 bg-transparent text-slate-200 hover:bg-slate-800">
              <LogOut className="h-4 w-4 mr-1.5" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="font-display text-2xl sm:text-3xl font-bold text-white mb-1">Leads & Bookings</h1>
        <p className="text-slate-400 mb-7">Newest first. Update each lead's status as you follow up.</p>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {metrics.map((m) => (
            <div key={m.label} data-testid={`metric-${m.label.split(" ")[0].toLowerCase()}`} className="rounded-xl border border-slate-700/80 bg-[#0A142F] p-5">
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-400 uppercase tracking-wide">{m.label}</span>
                <m.icon className={`h-5 w-5 ${m.color}`} />
              </div>
              <div className="font-display text-3xl font-extrabold text-white mt-2">{m.value}</div>
            </div>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
            <Input data-testid="dashboard-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name, email, phone..." className="pl-9 bg-[#0A142F] border-slate-700" />
          </div>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger data-testid="dashboard-type-filter" className="w-full sm:w-44 bg-[#0A142F] border-slate-700"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#0A142F] border-slate-700 text-slate-100">
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="quote">Quote Requests</SelectItem>
              <SelectItem value="survey">Site Surveys</SelectItem>
              <SelectItem value="contact">Contact Messages</SelectItem>
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="dashboard-lead-status-filter" className="w-full sm:w-44 bg-[#0A142F] border-slate-700"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#0A142F] border-slate-700 text-slate-100">
              <SelectItem value="all">All Statuses</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="py-24 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#38BDF8]" /></div>
        ) : filtered.length === 0 ? (
          <div className="py-24 text-center" data-testid="dashboard-empty">
            <Inbox className="h-12 w-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400">No leads match your filters yet.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="dashboard-lead-table">
            {filtered.map((s) => (
              <LeadCard key={s.id} lead={s} onStatus={updateStatus} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

function LeadCard({ lead, onStatus }) {
  const meta = TYPE_META[lead.type] || TYPE_META.contact;
  const Icon = meta.icon;
  const date = lead.created_at ? new Date(lead.created_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "";

  return (
    <div data-testid={`lead-card-${lead.id}`} className="rounded-xl border border-slate-700/80 bg-[#0A142F] p-5 card-hover">
      <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2.5 mb-2 flex-wrap">
            <span className={`flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide ${meta.color}`}>
              <Icon className="h-4 w-4" /> {meta.label}
            </span>
            <Badge variant="outline" className={`capitalize text-xs ${STATUS_STYLES[lead.status] || STATUS_STYLES.new}`}>{lead.status}</Badge>
            <span className="text-xs text-slate-500">{date}</span>
          </div>
          <h3 className="font-display text-lg font-semibold text-white">{lead.name}</h3>
          <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1.5 text-sm text-slate-400">
            {lead.email && <a href={`mailto:${lead.email}`} className="flex items-center gap-1.5 hover:text-[#38BDF8]"><Mail className="h-3.5 w-3.5" /> {lead.email}</a>}
            {lead.phone && <a href={`tel:${lead.phone}`} className="flex items-center gap-1.5 hover:text-[#38BDF8]"><Phone className="h-3.5 w-3.5" /> {lead.phone}</a>}
            {lead.company && <span className="flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5" /> {lead.company}</span>}
            {lead.site_address && <span className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> {lead.site_address}</span>}
          </div>

          {lead.services?.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {lead.services.map((svc) => (
                <span key={svc} className="text-xs rounded-md bg-[#0066FF]/10 text-[#38BDF8] border border-[#38BDF8]/20 px-2 py-0.5">{svc.split(" ")[0]}</span>
              ))}
            </div>
          )}

          <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-xs text-slate-500">
            {lead.property_type && <span><span className="text-slate-400">Property:</span> {lead.property_type}</span>}
            {lead.urgency && <span><span className="text-slate-400">Urgency:</span> {lead.urgency}</span>}
            {lead.preferred_date && <span><span className="text-slate-400">Preferred:</span> {lead.preferred_date} {lead.preferred_time}</span>}
          </div>

          {lead.message && <p className="mt-3 text-sm text-slate-300 bg-[#050B18] rounded-lg p-3 border border-slate-800">{lead.message}</p>}
        </div>

        <div className="lg:w-48 shrink-0">
          <Label className="text-xs text-slate-500 uppercase tracking-wide">Status</Label>
          <Select value={lead.status} onValueChange={(v) => onStatus(lead.id, v)}>
            <SelectTrigger data-testid={`lead-status-select-${lead.id}`} className="mt-1.5 bg-[#050B18] border-slate-700 capitalize"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#0A142F] border-slate-700 text-slate-100">
              {STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}
